import ISO6391 from 'iso-639-1';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string, InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { timeZonesNames } from '@vvo/tzdb';
import {
  availableLanguages,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cultureValueSchema,
  DD,
  DT,
  FormField,
  GridCol,
  GridRow,
  Input,
  isDefinedString,
  LanguageCode,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
} from '@client/toolkit';
import { usePutPersonsByIdMutation } from '@client/api';
import { useSession } from '../../../hooks';
import { useTenants } from '../../../hooks/useTenants/useTenants';
import { TenantSelectionEnum } from '../../../redux';

const schema = object({
  firstName: string().required(),
  lastName: string().required(),
  phone: string().nullable().default(null),
  culture: cultureValueSchema,
});

type FormValues = InferType<typeof schema>;

const ALLOWED_LANGUAGE_CODES: LanguageCode[] = availableLanguages.map((x) => x.code);
const LANGUAGES = ISO6391.getAllCodes()
  .map((x) => ({ code: x, name: ISO6391.getName(x) }))
  .filter((x) => ALLOWED_LANGUAGE_CODES.includes(x.code))
  .sort((a, b) => a.name.localeCompare(b.name));

export const Me = () => {
  const { t: tGlobal } = useTranslation('global');
  const { t: tCommon } = useTranslation();
  const { t: tType } = useTranslation('type');

  const { sessionPerson, sessionUser } = useSession();
  const { selectedTenant, allowedTenants } = useTenants();
  const [isEditing, setIsEditing] = useState(false);
  const [putPerson, { isLoading: isSaving }] = usePutPersonsByIdMutation();

  // Role is inherently tenant-specific — only shown when a tenant is selected
  const tenant = selectedTenant && selectedTenant !== TenantSelectionEnum.All ? selectedTenant : null;

  const sortedTenants = useMemo(
    () => [...(allowedTenants ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [allowedTenants],
  );

  const defaultValues = useMemo(
    () => ({
      firstName: sessionPerson?.firstName ?? '',
      lastName: sessionPerson?.lastName ?? '',
      phone: sessionPerson?.phone ?? '',
      culture: {
        language: sessionPerson?.culture?.language ?? '',
        timezone: sessionPerson?.culture?.timezone ?? '',
      },
    }),
    [
      sessionPerson?.firstName,
      sessionPerson?.lastName,
      sessionPerson?.phone,
      sessionPerson?.culture?.language,
      sessionPerson?.culture?.timezone,
    ],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    reset(defaultValues);
    setIsEditing(false);
  }, [reset, defaultValues]);

  const handleSave = async (data: FormValues) => {
    if (!sessionPerson?.id) return;

    await putPerson({
      id: sessionPerson.id,
      body: {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone || null,
        culture: data.culture,
        email: sessionPerson.email,
      },
    }).unwrap();

    setIsEditing(false);
  };

  const fullName = [sessionPerson?.firstName, sessionPerson?.lastName].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-6">
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleSave)}>
          <Card>
            <CardHeader>
              <CardTitle className="flex-1" variant="default">
                <Text className="text-xl font-medium text-white" isEmpty={!fullName}>
                  {fullName}
                </Text>
              </CardTitle>
              {!isEditing ? (
                <Button color="primary-dark" size="sm" onClick={handleEdit}>
                  {tCommon('edit')}
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button type="submit" color="primary" size="sm" disabled={isSaving}>
                    {tCommon('save')}
                  </Button>
                  <Button color="primary-dark" size="sm" onClick={handleCancel} disabled={isSaving}>
                    {tCommon('cancel')}
                  </Button>
                </div>
              )}
            </CardHeader>

            <CardContent>
              <div className="mb-6">
                <Text className="font-bold uppercase text-xs tracking-tight text-brand-primary mb-4">
                  {tGlobal('profile.information')}
                </Text>

                <GridRow cols={3} spacingY={2} spacingX={2}>
                  <GridCol span={1}>
                    <DT>{tGlobal('user.firstName')}</DT>
                  </GridCol>
                  <GridCol span={2}>
                    {isEditing ? (
                      <div className="max-w-xs">
                        <FormField name="firstName" render={({ field }) => <Input {...field} />} />
                      </div>
                    ) : (
                      <DD isEmpty={!isDefinedString(sessionPerson?.firstName)}>{sessionPerson?.firstName}</DD>
                    )}
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('user.lastName')}</DT>
                  </GridCol>
                  <GridCol span={2}>
                    {isEditing ? (
                      <div className="max-w-xs">
                        <FormField name="lastName" render={({ field }) => <Input {...field} />} />
                      </div>
                    ) : (
                      <DD isEmpty={!isDefinedString(sessionPerson?.lastName)}>{sessionPerson?.lastName}</DD>
                    )}
                  </GridCol>
                </GridRow>
              </div>

              <div className="mb-6">
                <GridRow cols={3} spacingY={2} spacingX={2}>
                  <GridCol span={1}>
                    <DT>{tGlobal('user.language')}</DT>
                  </GridCol>
                  <GridCol span={2}>
                    {isEditing ? (
                      <div className="max-w-xs">
                        <FormField
                          name="culture.language"
                          render={({ field }) => (
                            <Select {...field}>
                              <option value="" />
                              {LANGUAGES.map((x) => (
                                <option key={x.code} value={x.code}>
                                  {x.name}
                                </option>
                              ))}
                            </Select>
                          )}
                        />
                      </div>
                    ) : (
                      <DD isEmpty={!isDefinedString(sessionPerson?.culture?.language)}>
                        {sessionPerson?.culture?.language ? tType(`Language.${sessionPerson.culture.language}`) : '-'}
                      </DD>
                    )}
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('user.timezone')}</DT>
                  </GridCol>
                  <GridCol span={2}>
                    {isEditing ? (
                      <div className="max-w-xs">
                        <FormField
                          name="culture.timezone"
                          render={({ field }) => (
                            <Select {...field}>
                              <option value="" />
                              {timeZonesNames.map((timezone, i) => (
                                <option key={`${timezone}-${i}`} value={timezone}>
                                  {timezone}
                                </option>
                              ))}
                            </Select>
                          )}
                        />
                      </div>
                    ) : (
                      <DD isEmpty={!isDefinedString(sessionPerson?.culture?.timezone)}>
                        {sessionPerson?.culture?.timezone}
                      </DD>
                    )}
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('user.email')}</DT>
                  </GridCol>
                  <GridCol span={2}>
                    {isEditing ? (
                      <div className="max-w-xs">
                        <Input
                          value={sessionUser?.email ?? ''}
                          disabled
                          className="disabled:border-border disabled:text-gray-500"
                        />
                      </div>
                    ) : (
                      <DD isEmpty={!isDefinedString(sessionUser?.email)}>
                        <a href={`mailto:${sessionUser?.email}`} className="text-brand-primary hover:underline">
                          {sessionUser?.email}
                        </a>
                      </DD>
                    )}
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('user.phone')}</DT>
                  </GridCol>
                  <GridCol span={2}>
                    {isEditing ? (
                      <div className="max-w-xs">
                        <FormField
                          name="phone"
                          render={({ field }) => <Input {...field} value={field.value ?? ''} />}
                        />
                      </div>
                    ) : (
                      <DD isEmpty={!isDefinedString(sessionPerson?.phone)}>{sessionPerson?.phone}</DD>
                    )}
                  </GridCol>
                </GridRow>
              </div>
            </CardContent>
          </Card>
        </form>
      </FormProvider>

      <Card>
        <CardHeader>
          <CardTitle className="flex-1" variant="default">
            <Text className="text-lg font-medium text-white">{tGlobal('navigation.profile.myCompanies')}</Text>
          </CardTitle>
        </CardHeader>
        <CardContent variant="fill" className="rounded-lg w-full px-4 pb-4">
          <Table className="overflow-hidden w-full rounded-md" size="xs" layout="fixed">
            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{tGlobal('navigation.profile.company')}</TableHeadCell>
                <TableHeadCell>{tGlobal('user.role')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {sortedTenants.map((t) => {
                const isActive = tenant?.id === t.id;
                return (
                  <TableRow key={t.id} className={isActive ? 'bg-brand-primary/10' : ''}>
                    <TableCell>
                      <Text
                        className={isActive ? 'text-brand-primary font-semibold' : ''}
                        isEmpty={!isDefinedString(t.name)}
                      >
                        {t.name}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text
                        className={isActive ? 'text-brand-primary font-semibold' : ''}
                        isEmpty={!isDefinedString(t.role)}
                      >
                        {t.role ? tType(`Role.${t.role}`) : '-'}
                      </Text>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
